import {
  Icon,
  Utils,
  MenuProps,
  TextColor,
  ChatMessage,
  ButtonColors,
  OfficeMessage,
  ChatSectionProps,
  OfficeChatPuprose,
  StandardButtonProps,
} from '@gv/triage-components';

import { Routes } from 'config';
import { generateUrl } from 'utils/helpers';

import { ChatDialogActionType } from '../types';

import {
  ChatButton,
  SectionType,
  GetButtonsProps,
  RightSectionType,
  GetHeaderButtonsProps,
} from './types';

const { Home } = Routes.Cases.Details;

const isSectionType = (
  text: string | SectionType | ChatDialogActionType
): text is SectionType =>
  text ? (Object.values(RightSectionType) as string[]).includes(text) : false;

const isDialogType = (
  text: string | SectionType | ChatDialogActionType
): text is ChatDialogActionType =>
  text
    ? (Object.values(ChatDialogActionType) as string[]).includes(text)
    : false;

export const getButtons = ({
  caseId,
  isCompact,
  chatPurpose,
  isVirtualExam,
  setActionType,
  setSectionType,
  hasPaymentPermission,
}: GetButtonsProps) => {
  const isCaseExist = !!caseId;
  const rest: ChatButton[] = [
    {
      icon: <Icon.CheckCircle />,
      colorType: ButtonColors.Green,
      text: ChatDialogActionType.CompleteChat,
    },
  ];

  if (chatPurpose === OfficeChatPuprose.FrontDesk) {
    rest.splice(0, 0, {
      icon: <Icon.ChatPlacedBack />,
      text: ChatDialogActionType.PlaceBack,
    });
  }

  if (hasPaymentPermission) {
    rest.splice(0, 0, {
      icon: <Icon.CoinDollar />,
      text: RightSectionType.SendPaymentLink,
    });
  }

  let buttons: ChatSectionProps['buttons'] = [
    isCaseExist
      ? { icon: <Icon.Case />, text: RightSectionType.ViewCase }
      : {
          icon: <Icon.CheckCircle />,
          text: isVirtualExam
            ? RightSectionType.CreateCase
            : RightSectionType.CreateTask,
        },
  ];

  if (isCompact) {
    const menuItems = rest.map(({ text }) => ({
      text,
      onClick: () =>
        text === ChatDialogActionType.CompleteChat && !isCaseExist
          ? setSectionType(RightSectionType.ChatReport)
          : isSectionType(text)
            ? setSectionType(text)
            : setActionType(text),
    }));
    buttons.push({
      menuItems,
      icon: <Icon.More />,
    });
  } else {
    buttons = buttons.concat(rest);
  }

  const onClick = (item: StandardButtonProps, hasCase?: boolean) => {
    if (!isDialogType(item.text)) {
      if (isSectionType(item.text)) {
        return setSectionType(item.text);
      } else {
        return console.error('ERROR: This type is not supported');
      }
    }

    if (item.text === ChatDialogActionType.CompleteChat && !hasCase) {
      setSectionType(RightSectionType.ChatReport);
    } else {
      setActionType(item.text);
    }
  };

  return buttons.map((item) => {
    const isViewCase = item.text === RightSectionType.ViewCase;
    return item?.text
      ? {
          ...item,
          onClick: () => (isViewCase ? undefined : onClick(item, isCaseExist)),
          to: isViewCase && caseId ? generateUrl(Home, { caseId }) : undefined,
        }
      : item;
  });
};

export const getHeaderButtons = ({
  chat,
  mobile,
  tablet,
  onMute,
  isAdmin,
  isPetOwner,
  // onExport,
  // onTransfer,
  onAddMember,
  onVideoCall,
  parseOptions,
  onDeleteChat,
}: GetHeaderButtonsProps) => {
  if (isPetOwner) {
    return [];
  }
  const { isMuted } = Utils.Helpers.getOfficeChatProps(chat, parseOptions);

  const data: MenuProps['data'] = [
    { onClick: onMute, text: `${isMuted ? 'Unmute' : 'Mute'} chat` },
    // TODO: uncomment
    // { text: 'Export chat', onClick: onExport },
  ];

  if (isAdmin) {
    data.push({
      text: 'Delete chat',
      onClick: onDeleteChat,
      textColor: TextColor.Red,
    });
  }

  if (mobile || tablet) {
    data.push({
      text: 'Add members',
      onClick: onAddMember,
    });
  }

  if ((mobile || tablet) && onVideoCall) {
    data.push({ text: 'Video call', onClick: onVideoCall });
  }

  // TODO: uncomment this when backend was ready
  // if (mobile && !isChannel && hasPO) {
  //   data.push({
  //     onClick: onTransfer,
  //     text: 'Transfer chat',
  //   });
  // }

  const moreOption = {
    data,
    icon: <Icon.More />,
  };

  if (mobile || tablet) {
    return [moreOption];
  }

  const options = [
    {
      text: 'Add member',
      onClick: onAddMember,
      icon: <Icon.AddUser />,
      colorType: ButtonColors.Primary,
    },
    moreOption,
  ];

  if (onVideoCall) {
    options.splice(1, 0, {
      onClick: onVideoCall,
      icon: <Icon.Webcamera />,
      text: 'Start virtual exam',
      colorType: ButtonColors.Primary,
    });
  }

  // TODO: uncomment this when backend was ready
  // if (!isChannel) {
  //   if (hasPO) {
  //     options.splice(2, 0, { onClick: onTransfer, icon: <Icon.Transfer /> });
  //   }
  // }

  return options;
};

export const parseMessage = (value: OfficeMessage): ChatMessage => {
  const {
    id,
    text,
    author,
    isRead,
    isError,
    isPinned,
    isSystem,
    mentions,
    reactions,
    createdAt,
    updatedAt,
    isDeleted,
    isSending,
    formatting,
    groupedMessages,
    forwardedMessage,
    groupedMessageId,
    threadedMessageId,
    threadedMessagesCount,
  } = value || {};

  const author_id = author?.user?.id ?? author?.client?.id;

  let data: ChatMessage = {
    id,
    isRead,
    isError,
    mentions,
    isPinned,
    reactions,
    isDeleted,
    isSending,
    thread_id: '',
    groupedMessages,
    media_url: null,
    groupedMessageId,
    media_type: null,
    threadedMessageId,
    msg_text: text || '',
    threadedMessagesCount,
    created_at: createdAt,
    updated_at: updatedAt,
    displayAsHeader: isSystem,
    author_id: author_id ?? null,
    state: {
      author: Utils.Helpers.getChatUserInfo(
        author?.user ?? author?.client ?? {}
      ),
    },
  };

  if (formatting) {
    const html = Utils.Quill.convertDeltaToHTML(formatting);
    data.formatting = formatting;
    data.html = html;
  }

  if (forwardedMessage) {
    data.forwardedMessage = parseMessage(forwardedMessage);
  }

  return data;
};
